import {
  Button,
  ConfirmModal,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import { type gqlV2, useMutation, useTrpcMutation } from '@vrplatform/data';
import { useInfiniteQuery, useQuery } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { showWarnNotification } from '@vrplatform/theme';
import type { SelectItem } from '@vrplatform/components';
import type { Maybe } from '@vrplatform/utils';
import { Center, Text, Title } from '@mantine/core';
import { Box, Group, Modal, rem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { formatOwnerName, whereOwners } from '@vrplatform/ui-common';
import { useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useOwnerDetailDrawer } from '../../owner-drawers';

interface ModalProps {
  opened: boolean;
  closeModal: () => void;
  owner: Maybe<{
    id: string;
    name: Maybe<string>;
    isArchived: boolean;
    tenantId: string;
  }>;
}

export const LegacyOwnerEllipsisMenuModals = ({
  deleteModal,
  archiveModal,
  migrateModal,
  owner: initial,
}: {
  owner: ModalProps['owner'];
  deleteModal: {
    opened: boolean;
    closeModal: () => void;
  };
  migrateModal: {
    opened: boolean;
    closeModal: () => void;
  };
  archiveModal: {
    opened: boolean;
    closeModal: () => void;
  };
}) => {
  const [debounced] = useDebouncedValue(initial, 500);

  const owner = initial || debounced;

  return (
    <>
      <Archive {...archiveModal} owner={owner} />
      <Delete {...deleteModal} owner={owner} />
      <MigrateOwnerModal {...migrateModal} owner={owner} />
    </>
  );
};

export const Archive = ({ closeModal, opened, owner }: ModalProps) => {
  const { mutate, loading } = useMutation(
    (q, args: { ownerId: string; status: gqlV2.activeStatus_enum }) => {
      return q.updateOwner({
        pk_columns: { id: args.ownerId },
        _set: { status: args.status },
      })?.status;
    },
    {
      invalidateQueryKeys: ['owners'],
      errorMessage: {
        title: 'Failed to update owner',
      },
    }
  );

  const ownerId = owner?.id;
  const ownerName = owner?.name;
  const isArchived = owner?.isArchived;

  const archiveOwner = async () => {
    if (!ownerId) return showWarnNotification({ message: 'Missing owner' });

    await mutate({
      args: {
        ownerId,
        status: !isArchived ? 'inactive' : 'active',
      },
    });

    closeModal();
  };

  return (
    <ConfirmModal
      type="confirm"
      closeModal={closeModal}
      onSubmit={archiveOwner}
      opened={opened}
      title={`Are you sure you want to ${!isArchived ? 'archive' : 'unarchive'} ${ownerName || 'this owner'}?`}
      icon={'FolderOpenIcon'}
      loading={loading}
      zIndex={300}
      size={450}
      subtitle={
        !isArchived
          ? 'The owner will be archived and hidden behind a table filter. Owners users will retain access to the data.'
          : "The owner will be unarchived and visible in the owner's table."
      }
      submitTitle={!isArchived ? 'Archive' : 'Unarchive'}
    />
  );
};

export const Delete = ({ closeModal, opened, owner }: ModalProps) => {
  const { close: closeOwnerDetailDrawer } = useOwnerDetailDrawer();
  const { mutate, loading } = useTrpcMutation('deleteOwner', {
    invalidateQueryKeys: ['owners'],
    successMessage: {
      title: 'Success!',
      message: 'Owner was deleted successfully.',
    },
    errorMessage: {
      title: 'Failed to delete owner',
    },
  });

  const ownerId = owner?.id;
  const ownerName = owner?.name;
  const tenantId = owner?.tenantId;

  const deleteOwner = async () => {
    if (!ownerId || !tenantId) return;

    await mutate({ ownerId, tenantId });
    closeModal();
    closeOwnerDetailDrawer();
  };

  return (
    <ConfirmModal
      type="delete"
      closeModal={closeModal}
      onSubmit={deleteOwner}
      loading={loading}
      opened={opened}
      subtitle="The owner will be entirely removed from your team."
      title={`Are you sure you want to delete ${ownerName || 'this owner'}?`}
      zIndex={300}
    />
  );
};

type FormInputs = {
  newOwnerId: string;
};

const MigrateOwnerModal = ({ closeModal, opened, owner }: ModalProps) => {
  const { open: openOwnerDetailDrawer } = useOwnerDetailDrawer();

  const ownerId = owner?.id;
  const ownerName = owner?.name;
  const tenantId = owner?.tenantId;

  const methods = useForm<FormInputs>();

  const { mutate } = useTrpcMutation('migrateOwner', {
    invalidateQueryKeys: ['owners'],
    successMessage: {
      title: 'Success!',
      message: 'Owner data was successfully migrated.',
    },
  });

  const onClose = () => {
    methods.reset();
    closeModal();
  };

  const submit = (values: FormInputs) => {
    if (!ownerId || !tenantId) return;

    return mutate({
      newOwnerId: values.newOwnerId,
      oldOwnerId: ownerId,
      tenantId,
    }).then((res) => {
      onClose();
      if (res.ok) openOwnerDetailDrawer(values.newOwnerId);
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      zIndex={300}
      radius="md"
      centered
      styles={{
        title: {
          fontWeight: 500,
        },
        body: {
          padding: rem(32),
        },
      }}
    >
      <Center mb="lg">
        <Icon
          icon="ShuffleIcon"
          size={24}
          color={({ colors, primaryColor }) => colors[primaryColor][5]}
        />
      </Center>
      <Title ta="center" order={4}>
        Migrate data to different owner
      </Title>

      <FormProvider {...methods}>
        <Box
          mt="xs"
          component="form"
          onReset={onClose}
          onSubmit={methods.handleSubmit(submit)}
        >
          <Text component="p" ta="center" mb="xs">
            Migrating will transfer all statements, ownerships and user access
            from {ownerName ? <b>{ownerName}</b> : 'the old owner'} to the new
            owner.
          </Text>
          {ownerId && tenantId && (
            <OwnerSelect ownerId={ownerId} tenantId={tenantId} />
          )}
          <Group mt="xl">
            <Button
              type="reset"
              sx={{
                flex: 1,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              sx={{
                flex: 3,
              }}
            >
              Migrate data
            </Button>
          </Group>
        </Box>
      </FormProvider>
    </Modal>
  );
};

const OwnerSelect = ({
  ownerId,
  tenantId,
}: {
  tenantId: string;
  ownerId: string;
}) => {
  const [search, setSearch] = useState('');

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      const where: gqlV2.owner_bool_exp = {
        ...whereOwners({ teamId: args.tenantId, search: args.search }),
        id: { _neq: args.ownerId },
      };

      const list = q
        .owners({
          where,
          limit,
          offset,
          order_by: [{ name: 'asc_nulls_last' }],
        })
        .map<SelectItem>((owner) => ({
          label: formatOwnerName(owner),
          value: owner.id,
          icon: (
            <Icon
              icon={owner.type === 'company' ? 'OfficeIcon' : 'UserIcon'}
              size={16}
            />
          ),
        }));

      const aggregate = q.ownerAggregate({ where }).aggregate?.count() || 0;
      return {
        list,
        aggregate,
      };
    },
    {
      variables: {
        ownerId,
        tenantId,
        search: search?.trim(),
      },
    }
  );

  const { control } = useFormContext<FormInputs>();

  return (
    <Controller
      control={control}
      name="newOwnerId"
      rules={{
        required: 'Please select an owner to migrate to',
      }}
      render={({ field, fieldState: { error } }) => {
        const { data, isLoading: loadingValue } = useQuery(
          (q, args) => {
            return (
              q
                .owners({
                  where: {
                    id: { _eq: args.ownerId },
                  },
                  limit: 1,
                })
                .map((owner) => ({
                  label: formatOwnerName(owner),
                  value: owner.id,
                  icon: (
                    <Icon
                      icon={
                        owner.type === 'company' ? 'OfficeIcon' : 'UserIcon'
                      }
                      size={16}
                    />
                  ),
                }))[0] || null
            );
          },
          {
            skip: !field.value,
            variables: {
              ownerId: field.value,
              tenantId,
              search: search?.trim(),
            },
          }
        );

        return (
          <InputWrapper
            label="Owner"
            maw={350}
            mx="auto"
            error={error?.message}
          >
            <InputSelect
              value={data || null}
              setValue={(value) => field.onChange(value?.value || null)}
              type="single"
              infiniteData={{ ...queryData, setSearch }}
              inputProps={{
                placeholder: 'Select owner',
                error: !!error,
                loadingQuery: loadingValue,
              }}
              dropdownProps={{
                withinPortal: true,
                width: 'target',
              }}
            />
          </InputWrapper>
        );
      }}
    />
  );
};
